import AttachmentStatus from "./enums/AttachmentStatus.js";
import AttachmentType from "./enums/AttachmentType";
import IAttachments from "./interfaces/IAttachments.js";
import {AllPermitsResult} from "./types/AllPermitsResult";
import {AttachmentResult} from "./types/AttachmentResult.js";
import {EntityDocument} from "./types/EntityDocument";

function findExpiredPrivate(entity: IAttachments | undefined): AttachmentResult[] {
	if (entity == null) return [];
	return entity.attachments.filter(x => x.status === AttachmentStatus.Expired || x.status === AttachmentStatus.Rejected);
}

function createAttachmentEntries<T extends IAttachments, E extends "residency" | "driver" | "permit" | "vehicle">(entities: T[], entityType: E): Array<{
	attachment: AttachmentResult;
	entityType: E;
	entity: T
}> {
	return entities.flatMap(entity =>
		entity.attachments.map(attachment => ({
			attachment,
			entityType,
			entity
		}))
	);
}


class Attachments {
	static areApproved(entity: IAttachments): boolean {
		let attachments = entity.attachments;
		switch (attachments.length) {
			case 0: {
				return true;
			}
			case 1: {
				return this.isApproved(attachments[0]);
			}
		}

		for (let attachment of attachments) {
			if (!this.isApproved(attachment)) {
				return false;
			}
		}
		return true;
	}

	static areValid(entity: IAttachments): boolean {
		let attachments = entity.attachments;
		switch (attachments.length) {
			case 0: {
				return true;
			}
			case 1: {
				return this.isValid(attachments[0]);
			}
		}

		for (let attachment of attachments) {
			if (!this.isValid(attachment)) {
				return false;
			}
		}
		return true;
	}

	static findExpired(entities: (IAttachments | undefined)[]): AttachmentResult[];
	static findExpired(entity: IAttachments | undefined): AttachmentResult[];
	static findExpired(entities: IAttachments | undefined | ((IAttachments | undefined)[])): AttachmentResult[] {
		if (Array.isArray(entities)) {
			const attachments: AttachmentResult[] = [];
			for (const entity of entities) {
				attachments.push(...findExpiredPrivate(entity));
			}

			return attachments;
		}

		return findExpiredPrivate(entities);
	}

	static findExpiredTypes(entity: IAttachments | undefined, required: AttachmentType[]): AttachmentType[] {
		if (entity == null) return required;
		const valid = entity.attachments.filter(x => Attachments.isValid(x)).map(x => x.type);
		return required.filter(x => !valid.includes(x));
	}

	static findSubmitted(entity: IAttachments | undefined): AttachmentType[] {
		if (entity == null) return [];
		return entity.attachments.filter(x => x.status === AttachmentStatus.Submitted).map(x => x.type);
	}

	static getStatus(entity: IAttachments): AttachmentStatus {
		const attachments = entity.attachments;

		switch (attachments.length) {
			case 0:
				return AttachmentStatus.Undefined;

			case 1:
				return attachments[0].status;

			default:
				if (attachments.some(attachment => attachment.status === AttachmentStatus.Rejected)) {
					return AttachmentStatus.Rejected;
				}

				if (attachments.some(attachment => attachment.status === AttachmentStatus.Expired)) {
					return AttachmentStatus.Expired;
				}

				if (attachments.some(attachment => attachment.status === AttachmentStatus.Submitted)) {
					return AttachmentStatus.Submitted;
				}

				return AttachmentStatus.Approved;
		}
	}

	static isApproved(attachment: AttachmentResult): boolean {
		return attachment.status === AttachmentStatus.Approved;
	}

	static isValid(attachment: AttachmentResult): boolean {
		return attachment.status === AttachmentStatus.Submitted || attachment.status === AttachmentStatus.Approved;
	}

	static list(data: AllPermitsResult): EntityDocument[] {
		const holders = data.holder.attachments.map<EntityDocument>(attachment => ({
			attachment,
			entityType: "holder",
			entity: data.holder
		}));

		const residencies = createAttachmentEntries(data.residencies, "residency");
		const drivers = createAttachmentEntries(data.drivers, "driver");
		const permits = createAttachmentEntries(data.permits, "permit");
		const vehicles = createAttachmentEntries(data.vehicles, "vehicle");

		return [...holders, ...residencies, ...drivers, ...permits, ...vehicles];
	}
}


export {Attachments};
